/**
 * Analytics Portal V1 — the single browser data client for the protected read APIs
 * (Phase 9, plan §11 + §12.1). Every Analytics request in the app goes through here.
 *
 * Rules this module enforces so no component has to think about them:
 *   - **No authorization logic in the browser.** The server decides; we only render what it
 *     returns (`locked`, `403 analytics_forbidden`, capabilities, availability, notices).
 *   - **No persistence of protected data.** No localStorage, no sessionStorage, no
 *     IndexedDB, no cookies. Responses live in React Query's in-memory cache only.
 *   - **No payload logging.** Errors are normalized to a code + safe message; metric values
 *     are never written to the console.
 *   - **Cancellation:** every reader takes an AbortSignal, so changing tab, entity or range
 *     abandons the in-flight request instead of racing it.
 *   - **Bounded retries.** Callers pass `retry: 0/1`; nothing retries indefinitely.
 *   - The backend already sends `Cache-Control: private, no-store`; we never override it.
 */
import api from "@/lib/api";

export const TAB_KEYS = ["summary", "profile", "business", "capital", "events", "resources"];
export const DEFAULT_TAB = "summary";
// All six dashboards ship as of Phase 15, so no core tab shows a Coming soon state. The list and
// the `ComingSoon` component are kept (empty) as the rollback path for a tab that must be hidden.
export const COMING_SOON_TABS = [];

// tab -> the API segment and the entity type the tab is scoped to
export const TAB_SEGMENT = {
  profile: "profile", business: "business", capital: "capital",
  events: "event", resources: "resource",
};
export const TAB_ENTITY_TYPE = {
  profile: "personal", business: "business", capital: "capital",
  events: "event", resources: "resource",
};
export const CATEGORIES = ["all", "personal", "business", "capital", "event", "resource"];

export const ENTITY_TYPE_LABEL = {
  personal: "Personal profile", business: "Business", capital: "Capital profile",
  event: "Event", resource: "Resource",
};

const SAFE_MESSAGES = {
  analytics_disabled: "Analytics isn't available right now.",
  analytics_forbidden: "You don't have analytics access for this entity.",
  analytics_tier_required: "Analytics is included with PRO and CAPITAL.",
  analytics_unavailable: "We couldn't confirm your analytics access. Please try again.",
  analytics_rate_limited: "Too many requests. Please wait a moment and try again.",
  invalid_range: "That date range isn't valid. Choose a different range.",
  invalid_timezone: "That time zone isn't supported.",
  invalid_entity: "That entity couldn't be found in your analytics.",
  invalid_category: "That category isn't valid.",
  not_authenticated: "Please sign in again to view analytics.",
  not_found: "That analytics view doesn't exist.",
};

/** Normalize any failure into `{status, code, message, retryAfter}`. Never leaks payloads. */
export function normalizeAnalyticsError(error) {
  const status = error?.response?.status || 0;
  const detail = error?.response?.data?.detail;
  const code = (typeof detail === "object" ? detail?.code : null)
    || (status === 401 ? "not_authenticated" : null)
    || (status ? "request_failed" : "network_error");
  const retryAfter = Number(error?.response?.headers?.["retry-after"]) || null;
  return {
    status,
    code,
    canceled: error?.code === "ERR_CANCELED" || error?.name === "CanceledError",
    retryAfter,
    message: SAFE_MESSAGES[code]
      || (status >= 500 ? "Analytics is temporarily unavailable." : "Something went wrong loading analytics."),
  };
}

/** Query params shared by Summary and every entity dashboard. */
export function rangeParams({ preset, start, end, tz, compare } = {}) {
  const params = {};
  if (start && end) {
    params.start = start;
    params.end = end;
  } else if (preset && preset !== "custom") {
    params.preset = preset;
  }
  if (tz) params.tz = tz;
  if (compare === false) params.compare = false;
  return params;
}

export async function getAnalyticsConfig({ signal } = {}) {
  const { data } = await api.get("/analytics/config", { signal });
  return data;
}

export async function getManagedEntities({ category, signal } = {}) {
  const params = category && category !== "all" ? { category } : {};
  const { data } = await api.get("/analytics/entities", { params, signal });
  return data;
}

export async function getSummary({ category, range, signal } = {}) {
  const params = { ...rangeParams(range) };
  if (category && category !== "all") params.category = category;
  const { data } = await api.get("/analytics/summary", { params, signal });
  return data;
}

export async function getEntityDashboard({ tab, entityId, range, signal } = {}) {
  const segment = TAB_SEGMENT[tab];
  if (!segment || !entityId) return null;
  const { data } = await api.get(`/analytics/${segment}`, {
    params: { entity_id: entityId, ...rangeParams(range) }, signal,
  });
  return data;
}

/**
 * Endpoint 9 — named visitors for ONE personal/business/capital entity (CAPITAL+ only).
 * Deliberately a separate request from the dashboard, so a viewer without the capability never
 * receives identity data in any payload. Nothing here is persisted or logged.
 */
export async function getNamedVisitors({ entityType, entityId, range, sort, page, limit,
                                         filters, signal } = {}) {
  if (!entityType || !entityId) return null;
  const { data } = await api.get("/analytics/visitors", {
    params: {
      entity_type: entityType, entity_id: entityId, ...rangeParams(range),
      sort: sort || "recent", page: page || 1, limit: limit || 25,
      ...Object.fromEntries(Object.entries(filters || {})
        .filter(([, value]) => value && String(value).trim())
        .map(([key, value]) => [key, String(value).trim()])),
    },
    signal,
  });
  return data;
}

/**
 * Phase 18 — exports (plan §16, endpoints 12–13).
 *
 * One entity, one file, one request: there is no portfolio export, no multi-entity ZIP, no
 * recurring schedule, no share link and no Named Visitor export. The section keys below are the
 * server's own allowlist — the browser sends identifiers, never display labels, and the server
 * ignores anything it doesn't recognize. Report configuration travels in the POST body, so no
 * protected configuration ever appears in a URL, a referrer or browser history. The blob is
 * handed to the download and then revoked: nothing is written to localStorage, sessionStorage,
 * IndexedDB or a cookie, and no value is ever logged.
 */
export const PDF_SECTIONS = [
  { key: "executive_summary", label: "Executive summary", forced: true },
  { key: "kpi_cards", label: "Key metrics" },
  { key: "trend_charts", label: "Trends over time" },
  { key: "comparisons", label: "Previous-period comparison" },
  { key: "funnel", label: "Conversion funnel" },
  { key: "audience", label: "Audience breakdown" },
  { key: "traffic_sources", label: "Traffic sources" },
  { key: "metric_table", label: "Detailed metric table" },
];
export const PDF_SECTION_KEYS = PDF_SECTIONS.map((section) => section.key);
export const FORCED_PDF_SECTIONS = PDF_SECTIONS.filter((s) => s.forced).map((s) => s.key);

const FILENAME_RE = /filename="?([^"; ]+)"?/i;

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick so the browser has taken the blob, and nothing is retained.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Read the JSON error the server sent even though we asked for a binary response. */
async function decodeBlobError(error) {
  const body = error?.response?.data;
  if (body && typeof body.text === "function") {
    try {
      error.response.data = JSON.parse(await body.text());
    } catch {
      error.response.data = null;
    }
  }
  return error;
}

export async function exportAnalytics({ format, entityType, entityId, range, tz, sections,
                                        signal } = {}) {
  if (!entityType || !entityId) return null;
  const body = {
    entity_type: entityType,
    entity_id: entityId,
    compare: range?.compare !== false,
  };
  if (range?.start && range?.end) {
    body.start = range.start;
    body.end = range.end;
  } else if (range?.preset && range.preset !== "custom") {
    body.preset = range.preset;
  }
  if (tz) body.tz = tz;
  if (format === "pdf") {
    body.sections = (sections || PDF_SECTION_KEYS).filter((key) =>
      PDF_SECTION_KEYS.includes(key));
    FORCED_PDF_SECTIONS.forEach((key) => {
      if (!body.sections.includes(key)) body.sections.unshift(key);
    });
  }
  const fallback = format === "pdf" ? "analytics-report.pdf" : "analytics-export.zip";
  try {
    const response = await api.post(`/analytics/export/${format}`, body, {
      responseType: "blob", signal,
    });
    const match = FILENAME_RE.exec(response.headers?.["content-disposition"] || "");
    saveBlob(response.data, match ? match[1] : fallback);
    return true;
  } catch (error) {
    throw await decodeBlobError(error);
  }
}

export async function refreshAnalytics({ tab, entityId }) {
  const scope = TAB_SEGMENT[tab];
  if (!scope || !entityId) return null;
  const { data } = await api.post("/analytics/refresh", { scope, entity_id: entityId });
  return data;
}
